// ═══════════════════════════════════════════════════════════
// ARAMS UI — Skeleton, Separator, PageHeader, StatCard, EmptyState
// ═══════════════════════════════════════════════════════════

import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { Card } from './Card';

// ── Skeleton ──────────────────────────────────────────────
export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('bg-surface-800/60 rounded-lg shimmer', className)} {...props} />;
}

// ── Separator ─────────────────────────────────────────────
export function Separator({
  orientation = 'horizontal',
  className,
}: {
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}) {
  return (
    <div
      className={cn(
        'bg-surface-800/60',
        orientation === 'horizontal' ? 'h-px w-full' : 'w-px h-full',
        className,
      )}
    />
  );
}

// ── Page Header ───────────────────────────────────────────
interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  breadcrumb?: React.ReactNode;
}

export function PageHeader({ title, subtitle, actions, breadcrumb }: PageHeaderProps) {
  return (
    <div className="mb-8">
      {breadcrumb && <div className="mb-3">{breadcrumb}</div>}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">{title}</h1>
          {subtitle && <p className="text-surface-400 mt-1">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-3">{actions}</div>}
      </div>
    </div>
  );
}

// ── Stat Card ─────────────────────────────────────────────
interface StatCardProps {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  trend?: { value: number; label: string };
  color?: 'brand' | 'success' | 'danger' | 'warning' | 'info';
}

export function StatCard({ label, value, icon, trend, color = 'brand' }: StatCardProps) {
  return (
    <Card className="relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-surface-400">{label}</p>
          <p className="text-3xl font-bold mt-1 text-surface-100">{value}</p>
          {trend && (
            <p className={cn('text-xs mt-2', trend.value >= 0 ? 'text-success' : 'text-danger')}>
              {trend.value >= 0 ? '↑' : '↓'} {Math.abs(trend.value)}% {trend.label}
            </p>
          )}
        </div>
        {icon && (
          <div
            className={cn(
              'w-12 h-12 rounded-xl flex items-center justify-center',
              color === 'brand' && 'bg-brand-500/10 text-brand-400',
              color === 'success' && 'bg-success/10 text-success',
              color === 'danger' && 'bg-danger/10 text-danger',
              color === 'warning' && 'bg-warning/10 text-warning',
              color === 'info' && 'bg-info/10 text-info',
            )}
          >
            {icon}
          </div>
        )}
      </div>
      <div
        className={cn(
          'absolute top-0 right-0 w-32 h-32 opacity-10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2',
          color === 'brand' && 'bg-brand-500',
          color === 'success' && 'bg-success',
          color === 'danger' && 'bg-danger',
          color === 'warning' && 'bg-warning',
          color === 'info' && 'bg-info',
        )}
      />
    </Card>
  );
}

// ── Empty State ───────────────────────────────────────────
interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}

export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-20"
    >
      <div className="w-20 h-20 rounded-2xl bg-surface-800/50 flex items-center justify-center text-surface-500 mb-6">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-surface-300 mb-2">{title}</h3>
      <p className="text-surface-500 text-sm mb-6 text-center max-w-md">{description}</p>
      {action}
    </motion.div>
  );
}
